import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSidenavContent } from '@angular/material/sidenav';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject as RxjsSubject, zip } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { platform } from '../../platform/platform';
import { ApplicationError } from '../_errors/application-error';
import { Inspection } from '../_objects/inspection';
import {
  GeneratedReport,
  InspectionReport,
  InspectionReportList,
  InspectionReportListItem,
  InspectionReportReuse,
  PastReportVersion,
  PdfOptions,
  ReportImageQuality,
  ReportStatus
} from '../_objects/inspection-report';
import { MarkItem } from '../_objects/mark';
import { MeasurementModeName } from '../_objects/measurement-mode-name';
import { Subject } from '../_objects/subject';
import { SubjectLocation, SubjectModel, SubjectModelListItem } from '../_objects/subject-model';
import { Tags } from '../_objects/tags';
import { TitleItem } from '../_objects/title';
import { TenantUser } from '../_objects/user';
import { WkHtmlToPdfOptions } from '../_offline/models/report-preview/wkHtml-to-pdf-options';
import { IplexGConnectModeService } from '../_offline/services/iplex-g-connect-mode.service';
import { OfflineUtilsService } from '../_offline/services/offline-utils.service';
import { UtilsService } from '../_services/_utils.service';
import { CommonSnackBarService } from '../_services/common-snack-bar.service';
import { FileSystemAccessService } from '../_services/file-system-access.service';
import { InspectionReportService } from '../_services/http-api/inspection-report.service';
import { InspectionService } from '../_services/http-api/inspection.service';
import { ReportTemplateService } from '../_services/http-api/report-template.service';
import { SubjectModelService } from '../_services/http-api/subject-model.service';
import { SubjectService } from '../_services/http-api/subject.service';
import { TagsService } from '../_services/http-api/tags.service';
import { TenantConfigService } from '../_services/http-api/tenant-config.service';
import { TenantUserService } from '../_services/http-api/tenant-user.service';
import { LangUpdateService } from '../_services/lang-update.service';
import { MarksStateService } from "../_services/mark-state/marks-state.service";
import { NetworkStatusService } from '../_services/network-status.service';
import { OfflineReportPreviewService } from '../_services/offline-report-preview.service';
import { LoginUserOfflineService } from '../_services/offline/login-user-offline.service';
import { SpinnerService } from '../_services/spinner.service';
import { SelectReportService } from '../_services/select-report.service';
import { INSPECTION___PAST_REPORT_FILTER_PARAM, StorageService, SubjectFilter } from '../_services/storage.service';
import { TitlesStateService } from "../_services/title-state/titles-state.service";
import {
  GEN_DRAFT_REPORT_TYPE,
  GEN_FINAL_REPORT_TYPE,
  INSPECTION_REPORT_STATUS_CODE,
  MODEL_CODE,
  PDF_COMPRESS_PARAMS_HIGH,
  PDF_COMPRESS_PARAMS_LOW,
  PDF_COMPRESS_PARAMS_MEDIUM,
  PERMISSION_CODE,
  PLATFORM,
  PaperKind,
  TenantConfig
} from '../app.constants';
import { CommonConfirmDialogComponent } from '../common-dialog/common-confirm-dialog.component';
import { CommonShareDialogComponent } from '../common-dialog/common-share-dialog/common-share-dialog.component';
import { CommonLayoutService } from '../common-layout/common-layout.service';
import { InspectionDataManagementComponent } from '../inspection-data-management/inspection-data-management.component';
import { InspectionImageStateService } from '../inspection-data-management/inspection-images-state.service';
import {
  InspectionReportAddDialogComponent
} from '../inspection-data-management/inspection-report-add-dialog/inspection-report-add-dialog.component';
import { InspectionReportComponent } from '../inspection-report/inspection-report.component';
import { ReuseConfirmDialogComponent } from '../inspection-report/reuse-confirm-dialog/reuse-confirm-dialog.component';
import { DataUploadDialogComponent } from './dialogs/data-upload-dialog/data-upload-dialog.component';
import { FirstActionGuideDialogComponent, FirstActions } from './dialogs/first-action-guide-dialog/first-action-guide-dialog.component';
import { InspectionUpsertDialogComponent } from './dialogs/inspection-upsert-dialog/inspection-upsert-dialog.component';
import { InspectionDataManageService } from './inspection-data-manage.service';
import { BlobService } from '../_services/blob.service';
import { ImagesFiltersStateService } from '../_services/filter-state.service';
import { ProgressSnackBarService } from '../_services/progress-snack-bar.service';
import { InspectionReportStateService } from "../_services/reports/inspection-report-state.service";
import { FILE_SYSTEM } from "../_offline/electron.constants";
import FILE_PREFIX = FILE_SYSTEM.PATH.FILE_PREFIX;
import { BrianSynchronizedInspectionStateService } from '../_services/brian-synchronized-inspection-state/brian-synchronized-inspection-state.service';
import { FileManagerComponent } from '../common-dialog/file-manager/file-manager.component';
import { RoutePage } from '../app-routes.enum';

@Component({
    selector: 'app-inspection',
    templateUrl: './inspection.component.html',
    styleUrls: ['./inspection.component.scss'],
    standalone: false
})
export class InspectionComponent implements OnInit, OnDestroy, AfterViewInit {

  readonly screenMedium = 1024;
  readonly closedMenuWidth = 60;
  readonly openedMenuWidth = 310;
  readonly sidenavTypes = ['folder', 'report'];
  readonly mainContentsMarginLeftList = {
    closed: `margin-left: ${this.closedMenuWidth}px`,
    expand: `margin-left: ${this.openedMenuWidth}px`
  };
  readonly getReportTime = 10000;
  private url: string;
  private primaryRoute: string;
  readonly DEFAULT_PASTREPORT_FILTER_PARAM: string = 'subject';

  title = 'Inspection';
  subTitle = '';
  inspectionId: string;
  inspection: Inspection;
  inspectionImagesCount$: Observable<number>;
  inspectionImagesCount: number;
  reportId: string;
  report: InspectionReport;
  generatedReports: InspectionReportListItem[];
  generatedReportTree: GeneratedReport[];
  historyReports: InspectionReportListItem[] = [];
  subjectModel: SubjectModel;
  isReadOnlyFlag: boolean;
  pastReportFilter: string;
  isReportLoading: boolean = false;
  previewPdfHtml: string;

  // Masters
  subjectModels: SubjectModelListItem[];
  subjects: Subject[]; // All candidates
  marks: MarkItem[];
  titles: TitleItem[];
  tags: string[];
  sidenavType = this.sidenavTypes[0];
  isFirstAccess = true;
  selectedLocation: SubjectLocation;
  onUploaded$ = new RxjsSubject<boolean>();
  onDestroy$ = new RxjsSubject();
  private getReportTimer: NodeJS.Timeout;
  private measurementModeName: MeasurementModeName;

  // Layout
  scrollTop: number;
  sideContentHeight: number;
  sideNavOpened: boolean = false;
  manualToggled: boolean = false;
  mainContentsStyle: string = this.mainContentsMarginLeftList["closed"];
  pdfUrl: SafeResourceUrl;

  @ViewChild(InspectionDataManagementComponent) inspectionDataComponent: InspectionDataManagementComponent;
  @ViewChild(InspectionReportComponent) inspectionReportComponent: InspectionReportComponent;

  @ViewChild('mainContent') mainContent: MatSidenavContent;
  @ViewChild('sideContents') sideContents: MatSidenavContent;

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get isLinkedWithIPLEXG() {
    return this.isOffline && this.iplexGConnectModeService.isHttpsConnectMode;
  }

  get isReportView() {
    return this.inspDataManageService.isReportView;
  }

  get subjects4Inspections(): Subject[] {
    if (!this.subjects || !this.inspection) {
      return [];
    }

    return this.subjects.filter(subject =>
      this.inspection.subjectId === subject.id
    );
  }

  get subjectModel4Inspections(): SubjectModelListItem[] {
    if (!this.subjects || !this.inspection) {
      return [];
    }

    return this.subjectModels.filter(subjectModel =>
      this.inspection.subjectModelId === subjectModel.id
    );
  }

  get masterObs(): Observable<[SubjectModelListItem[], Subject[], MarkItem[], TitleItem[], Tags]> {
    return zip(
      this.subjectModelService.getSubjectModels(),
      this.subjectService.getSubjects(),
      this.marksStateService.markItems$,
      this.titleService.titleItems$,
      this.tagsService.getTags(),
    );
  }

  get isEdit(): boolean {
    return this.inspection

      && (this.inspection.permission && (this.inspection.permission & PERMISSION_CODE.WRITE) !== 0);
  }

  get isUnreferencedSharedLock() {
    // eslint-disable-next-line no-constant-binary-expression
    return (!this.inspection.sharedLockBy === null || this.inspection.sharedLockBy === '')
      && (platform.name === PLATFORM.WEB
        || this.inspection.sharedLockBy.toLowerCase() !== this.loginUserService.loginUser.id);
  }

  get isDraftProcessing(): boolean {
    return !this.utilsService.isNullOrUndefined(this.report)
      && this.report.status === INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING;
  }

  get isFinalProcessing(): boolean {
    return !this.utilsService.isNullOrUndefined(this.report)
      && this.report.status === INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING;
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private blobService: BlobService,
    private changeDetectorRef: ChangeDetectorRef,
    private commonLayoutService: CommonLayoutService,
    private dialog: MatDialog,
    private fileSystemAccessService: FileSystemAccessService,
    private inspDataManageService: InspectionDataManageService,
    private inspectionImageStateService: InspectionImageStateService,
    private inspectionService: InspectionService,
    private inspectionReportService: InspectionReportService,
    private iplexGConnectModeService: IplexGConnectModeService,
    private langService: LangUpdateService,
    private location: Location,
    private loginUserService: LoginUserOfflineService,
    private marksStateService: MarksStateService,
    private networkStatusService: NetworkStatusService,
    private offlineReportPreviewService: OfflineReportPreviewService,
    private offlineUtilsService: OfflineUtilsService,
    private oscSpinnerService: SpinnerService,
    private utilsService: UtilsService,
    private reportTemplateService: ReportTemplateService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private selectReportService: SelectReportService,
    private snackBar: CommonSnackBarService,
    private storageService: StorageService,
    private subjectModelService: SubjectModelService,
    private subjectService: SubjectService,
    private tagsService: TagsService,
    private tenantUserService: TenantUserService,
    private titleService: TitlesStateService,
    private translate: TranslateService,
    private tenantConfigService: TenantConfigService,
    private filterStateService: ImagesFiltersStateService,
    private progressSnackBar: ProgressSnackBarService,
    private inspectionReportStateService : InspectionReportStateService,
    private brianSyncService: BrianSynchronizedInspectionStateService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.measurementModeName = new MeasurementModeName(this.translate);
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(_ => {
        const indexOfQ = this.router.url.indexOf('?');
        if (indexOfQ > 0) {
          this.url = this.router.url.slice(0, indexOfQ);
          this.inspDataManageService.imageIdInQueryParam = this.router.url.slice(indexOfQ).replace('?imageId=', '');
        } else {
          this.url = this.router.url;
        }
        this.primaryRoute = this.parseRouteUrl(this.url)[1];
      });

    this.inspectionImagesCount$ = this.inspectionImageStateService
      .inspectionImages$
      .pipe(takeUntil(this.onDestroy$))
      .pipe(map(images => images.length))
      .pipe(tap(count => {
        this.inspectionImagesCount = count;
        if (this.isEdit && this.isFirstAccess && count == 0 && !this.inspDataManageService.isReportView) {
          this.openFirstAccessGuideDialog();
        }
        this.isFirstAccess = false;
      }));

  }

  updateInspection(inspection: Inspection): void {
    this.inspection = inspection;
  }

  ngOnInit() {
    this.oscSpinnerService.show();
    this.onInitProcess();
    this.setInspDataManageSubscribe();
    this.isReadOnly();
    this.changePastReportFilterSubscribe();
    this.pastReportFilter = this.getPastReportFilterParam();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    clearTimeout(this.getReportTimer);
    // Fix for the issue of the background color
    document.getElementsByTagName('main')[0].classList.remove('no-background');
  }

  isSharedLockByUser(inspection: Inspection) {
    return inspection.sharedLockBy?.toLowerCase() === this.loginUserService.loginUser.id;
  }

  getRedirectParam(): NavigationExtras {
    return {
      replaceUrl: true,
      queryParams: {
        offlineDataUpdate: this.isDesktop && !this.isOffline && this.isSharedLockByUser(this.inspection)
          ? true
          : null
      }
    };
  }

  setInspDataManageSubscribe() {
    // Test site selection event
    this.inspDataManageService
      .filterLocationObs()
      .pipe(takeUntil(this.onDestroy$), filter(x => x !== null))
      .subscribe(location => this.selectedLocation = location);

    this.filterStateService.inspectionImagesFilters$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (this.mainContent) {
          this.mainContent.getElementRef().nativeElement.scrollTop = 0;
        }
      }
      );

    // Report tab change event
    this.inspDataManageService
      .updateReportTabObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(_ => {
        this.loadGeneratedReport(this.inspectionId);
        this.loadHistoryReports(this.pastReportFilter);
      });

    // Report reedit event
    this.inspDataManageService
      .reEditReportObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(report => this.initReport(report.reportId));

    // Report information switching event
    this.inspDataManageService
      .changeReportObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(viewReport => {
        const param = this.getRedirectParam();
        this.router.navigate(['/inspection-data-management/report/' + viewReport.reportId], param);
        this.loadInspectionReport(viewReport.reportId, viewReport.version, true);
      });

    // Report display event
    this.inspDataManageService
      .viewReportObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(viewReport => {

        const param = this.getRedirectParam();
        this.location.replaceState('/inspection-report/' + viewReport.reportId);
        this.inspDataManageService.isReportView = true;
        if (this.report?.reportId !== viewReport.reportId
          || this.report?.version !== viewReport.version) {
          this.loadInspectionReport(viewReport.reportId, viewReport.version, true);
        }
      });

    // Image list display event
    this.inspDataManageService
      .viewImagesObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(_ => {
        this.location.replaceState(['/inspection-data-management/inspection', this.inspectionId].join('/'));
        this.inspDataManageService.isReportView = false;
      });

    // Report reuse event
    this.inspDataManageService
      .reuseReportObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(reportId => this.openReuseConfirmDialog(reportId));

    // Draft PDF generation event
    this.inspDataManageService
      .createDraftPdfObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(type => {
        if (type === GEN_DRAFT_REPORT_TYPE.DEFAULT) {
          this.createDraftPdfConfirmDialog();
          this.callCreatePDF(true);
        } else if (type === GEN_DRAFT_REPORT_TYPE.SHARE_IMAGES) {
          this.createDraftPdfAndSHareConfirmDialog();
          this.callCreatePDF(true);
        } else {
          this.cancelGeneratePdf(true);
        }
      });

    // Final PDF generation event
    this.inspDataManageService
      .createFinalPdfObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(type => {
        if (type === GEN_FINAL_REPORT_TYPE.CREATE) {
          this.callCreatePDF(false);
        } else if (type === GEN_FINAL_REPORT_TYPE.GET && this.isFinalProcessing) {
          this.checkReportStatus(this.report);
        } else if (type === GEN_FINAL_REPORT_TYPE.GET && !this.isFinalProcessing) {
          this.getReportPdfUrl(this.report);
        } else {
          this.cancelGeneratePdf(false);
        }
      });
  }

  getPastReportFilterParam(): string {
    const param = this.storageService.fetchPastReportFilterParam(INSPECTION___PAST_REPORT_FILTER_PARAM);

    return param ? param : this.DEFAULT_PASTREPORT_FILTER_PARAM;
  }

  changePastReportFilterSubscribe() {
    this.inspDataManageService
      .changePastReportFilterObs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(filter => {
        this.isReportLoading = true;
        this.pastReportFilter = filter;
        this.storageService.pushPastReportFilterParam(INSPECTION___PAST_REPORT_FILTER_PARAM, this.pastReportFilter);
        this.loadHistoryReports(this.pastReportFilter);
      }
      );
  }

  openMenu() {
    if (this.inspectionDataComponent) {
      this.snackBar.clearAll();
    }
  }

  onChangedReportId(reportId: string) {
    this.onReportRenderProcess(reportId);
  }

  onPreviewGenerated(previewPdfHtml: string) {
    this.previewPdfHtml = previewPdfHtml;
  }

  onChangedInspection() {
    this.onInitProcess();
  }

  onInitProcess() {
    const parameters: string[] = this.parseRouteUrl(this.url);

    switch (this.primaryRoute) {
      case 'inspection-data-management':
        this.sidenavType = this.sidenavTypes[0];
        this.inspDataManageService.isReportView = false;

        // URL parameter reading
        if (parameters[2] === 'inspection') {
          // When displaying inspection data
          const inspectionIds = parameters.filter((_, index) => 2 < index);
          this.initInspection(inspectionIds[0]);
        } else {
          // When displaying inspection report data
          const reportId = parameters[3];
          this.initReport(reportId);
        }
        break;
      case 'inspection-report':
        this.sidenavType = this.sidenavTypes[1];
        this.inspDataManageService.isReportView = true;
        // URL parameter reading
        this.initReport(parameters[2]);
        break;
    }
  }

  initInspection(inspectionId: string): void {
    if (!inspectionId) {
      throw new ApplicationError('InspectionIds is not exists.');
    }
    this.inspectionId = inspectionId;
    const obInspection = this.getInspection(inspectionId);
    zip(
      // Get inspection information
      zip(obInspection),

      // Get generated report information
      this.inspectionReportService
        .getInspectionReportsByInspectionIds([inspectionId], 20, 1),

      // Obtain the information necessary to initialize the screen from the service
      this.masterObs
    )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        val => {
          this.inspection = val[0][0];
          this.commonLayoutService.commonLayoutTitleWrapperEmit(this.inspection);
          this.generatedReports = val[1].items;
          this.setMasters(...val[2]);
        },
        error => {
          console.log(error);
        },
        () => {
          if (this.isUnreferencedSharedLock) {
            // Data acquisition will be interrupted and a dialog will be displayed if sharing is locked and the display is online
            const dialogRef = this.openSharedLockedConfirmDialog();
            dialogRef.afterClosed()
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(_ => this.router.navigate([RoutePage.InspectionList]));
          } else {
            this.subTitle = this.utilsService.getInspectionTitle(this.inspection);
            this.loadSubjectModel();
            this.loadHistoryReports(this.pastReportFilter);
            this.getReportVersions();
          }
        });
  }

  initReport(reportId: string): void {
    this.reportId = reportId;
    zip(
      // 検査レポート情報を取得
      this.getInspectionReport(this.reportId),

      // 画面の初期化に必要な情報をサービスから取得する
      this.masterObs
    )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        val => {
          this.setReport(val[0], true);
          this.setMasters(...val[1]);
        },
        error => {
          console.log(error);
        });
  }

  loadSubjectModel() {
    this.getSubjectModel(this.inspection)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(subjectModel => {
        this.subjectModel = subjectModel;
        if (!this.selectedLocation) {
          this.selectedLocation = new SubjectLocation();
        }
      });
  }

  onReportRenderProcess(reportId: string) {
    if (!reportId) {
      return;
    }

    this.reportId = reportId;
    this.loadInspectionReport(reportId, null, false, true);
  }

  onResize() {
    if (this.sideNavOpened) {
      this.sideContentsToggle(null, false);
    } else {
      if (!this.manualToggled) {
        this.sideContentsToggle(null, false);
      }
    }
    this.getSideContentHeight();
  }

  loadInspectionReport(reportId: string, version: number = null, isDisplaySpinner = false, isInit = false): void {
    if (isDisplaySpinner) {
      this.oscSpinnerService.show();
    }

    this.reportId = reportId;
    this.getInspectionReport(this.reportId, version)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(inspectionReport => {
        this.setReport(inspectionReport, isInit);

        if (isDisplaySpinner) {
          this.oscSpinnerService.hide();
        }
      });
  }

  setReport(inspectionReport: InspectionReport, isInit: boolean) {
    this.inspectionReportStateService.setInspectionReport(inspectionReport);
    this.reportId = inspectionReport.reportId;
    this.checkReportStatus(inspectionReport);
    if (isInit) {
      this.loadInspection(inspectionReport.inspectionIds[0]);
      this.loadGeneratedReport(this.inspectionId);
    }
  }

  parseRouteUrl(url: string): string[] {
    return url.split('/');
  }

  loadInspection(inspectionId: string) {
    this.inspectionId = inspectionId;
    this.getInspection(inspectionId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        inspection => this.inspection = inspection,
        error => console.log(error),
        () => {
          this.commonLayoutService.commonLayoutTitleWrapperEmit(this.inspection, this.report);
          if (this.isUnreferencedSharedLock) {
            // Data acquisition will be interrupted and a dialog will be displayed if sharing is locked and the display is online.
            const dialogRef = this.openSharedLockedConfirmDialog(true);
            dialogRef.afterClosed()
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(_ => this.router.navigate([RoutePage.InspectionReportList]));
          } else {
            this.title = this.translate.instant('inspection.loadInspectionsTitle');
            this.subTitle = this.utilsService.getInspectionTitle(this.inspection);

            // load subjectModel
            this.loadSubjectModel();
            this.loadHistoryReports(this.pastReportFilter);
          }
        }
      );
  }

  getInspection(inspectionId: string): Observable<Inspection> {
    if (!inspectionId) {
      throw new ApplicationError('InspectionId does not exist.');
    }

    return this.inspectionService.getInspection(inspectionId, false);
  }

  getInspectionReport(inspectionReportId: string, version: number = null): Observable<InspectionReport> {
    if (!inspectionReportId) {
      throw new ApplicationError('InspectionReportId is invalid.');
    }
    return this.inspectionReportService.getInspectionReport(inspectionReportId, version);
  }

  getUser(userId: string): Observable<TenantUser> {
    if (!userId) {
      throw new ApplicationError('userId is invalid.');
    }
    return this.tenantUserService.getUser(userId);
  }

  loadGeneratedReport(inspectionId: string) {
    if (!inspectionId) {
      throw new ApplicationError('InspectionId does not exist.');
    }
    this.inspectionReportService
      .getInspectionReportsByInspectionIds([inspectionId], 20, 1)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        data => this.generatedReports = data.items,
        error => console.log(error),
        () => this.getReportVersions()
      );
  }

  getSubjectModel(inspection: Inspection): Observable<SubjectModel> {
    if (!inspection) {
      throw new ApplicationError('Inspection does not exist.');
    }
    let subjectModelId: string;
    let locationVersion: number;
    if (subjectModelId !== inspection.subjectModelId) {
      if (subjectModelId) {
        // Multiple inspections targeting different SubjectModels are not supported.
        throw new ApplicationError('More than one SubjectModel is included.');
      } else {
        subjectModelId = inspection.subjectModelId;
        locationVersion = inspection.locationVersion;
      }
    }
    return this.subjectModelService.getSubjectModel(subjectModelId, locationVersion);
  }

  openNewInspectionReportDialog() {
    this.openMenu();
    const dialogRef = this.dialog.open(InspectionReportAddDialogComponent, {
      minWidth: '700px',
      minHeight: '336px',
      autoFocus: false,
      data: {
        inspectionIds: [this.inspectionId],
        subjectModelId: this.subjectModel.id,
        subjectModelName: this.subjectModel.name
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(report => {
        if (report) {
          this.setReport(report, true);
          this.sidenavType = this.sidenavTypes[1];
          this.selectReportService.setViewReport(report.id, report.version);
        }
      });
  }

  isEnableInformation(): boolean {
    return this.inspection && !this.includesDeletedInspections(this.inspection);
  }

  private includesDeletedInspections(inspection: Inspection): boolean {
    return inspection.isDeleted;
  }

  /**
   * If you do not have permission to update the report, or
   * Read-only if the associated check contains update privileges
   * @returns boolean
   */
  isReadOnly(): boolean {
    this.isReadOnlyFlag =
      (this.report
        && (!this.report.permission
          || (this.report.permission & PERMISSION_CODE.WRITE) === 0
        )
      )
      || this.inspection && !this.inspection.permission
      || (this.inspection?.permission & PERMISSION_CODE.WRITE) === 0;
    return this.isReadOnlyFlag;
  }

  editInspection() {
    if (!this.isEnableInformation) {
      return;
    }
    const dialogRef = this.openInspectionInfoDialog();

    // Update Subjects
    dialogRef.componentInstance
      .updateSubjects
      .pipe(takeUntil(this.onDestroy$), takeUntil(dialogRef.afterClosed()))
      .subscribe(subjects => {
        if (subjects) {
          this.setSubjects(subjects);
        }
      });

    // Inspection updates
    dialogRef.componentInstance
      .changedInspection
      .pipe(takeUntil(this.onDestroy$), takeUntil(dialogRef.afterClosed()))
      .subscribe(insp => {
        if (insp) {
          if (this.inspection.subjectModelId !== insp.subjectModelId) {
            this.getSubjectModel(this.inspection).subscribe(subjectModel => {
              this.subjectModel = subjectModel;
            });
          }

          this.inspection = insp;
          this.inspectionId = insp.id;
          this.subTitle = this.utilsService.getInspectionTitle(this.inspection);

          this.onChangedInspection();
        }
        dialogRef.close();
      });
  }

  openInspectionInfoDialog(): MatDialogRef<InspectionUpsertDialogComponent> {
    const dialogRef = this.dialog.open(InspectionUpsertDialogComponent, {
      minWidth: '600px',
      panelClass: 'dialog-scroll',
      data: {
        inspection: this.inspection,
        subjects: this.subjects,
        subjectModels: this.subjectModels,
        inspectionImageCount: this.inspectionImagesCount,
      }
    });
    return dialogRef;
  }

  setMasters(subjectModels: SubjectModelListItem[], subjects: Subject[],
    marks: MarkItem[], titles: TitleItem[], tags: Tags): void {
    this.setSubjectModels(subjectModels);

    this.setSubjects(subjects);

    this.marks = marks;

    this.titles = titles;
    this.tags = tags.items;
  }

  setSubjectModels(subjectModels: SubjectModelListItem[]) {
    // Model initial settings
    this.subjectModels = subjectModels.sort((subjectModel1, subjectModel2) => {
      if (subjectModel1.name < subjectModel2.name) {
        return -1;
      }
      if (subjectModel1.name > subjectModel2.name) {
        return 1;
      }
      return 0;
    });
  }

  setSubjects(subjects: Subject[]) {
    // S/M initial settings
    this.subjects = subjects.sort((subject1, subject2) => {
      if (subject1.serialNo < subject2.serialNo) {
        return -1;
      }
      if (subject1.serialNo > subject2.serialNo) {
        return 1;
      }
      return 0;
    });
  }

  dataUpload() {
    const dialogRef = this.openDataUploadDialog();

    dialogRef.componentInstance
      .uploaded
      .pipe(takeUntil(this.onDestroy$), takeUntil(dialogRef.afterClosed()))
      .subscribe(_ => {
        this.inspectionImageStateService.fetchAndUpdate(this.inspection.id);
        dialogRef.close();
      });
  }

  uploadedProcess() {
    this.onUploaded$.next(true);
    this.loadInspection(this.inspectionId);
  }

  openDataUploadDialog(): MatDialogRef<DataUploadDialogComponent | FileManagerComponent> {
    const dialogRef = this.dialog.open(DataUploadDialogComponent, {
      disableClose: true,
      height: this.isLinkedWithIPLEXG ? "464px" : "467px",
      panelClass: 'data-upload-dialog',
      data: {
        inspection: this.inspection
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      const btnEls = document.getElementsByClassName('cdk-program-focused');
      for (let i = 0; btnEls.length; i++) {
        btnEls[i].classList.remove('cdk-program-focused');
      }
    });

    return dialogRef;
  }

  /** Display initial access guidance dialog
   * @returns void
   */
  openFirstAccessGuideDialog(): void {
    const dialogRef = this.dialog.open(FirstActionGuideDialogComponent, {
      width: '700px',
      data: { isReadOnlyFlag: this.isReadOnlyFlag }

    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.onDestroy$), map(value => value?.toString().toLowerCase()))
      .subscribe((value: FirstActions) => {
        switch (value) {
          case 'download':
            this.editInspection();
            break;
          case 'synchronize':
            this.brianSyncService.setBrianSynchronizedInspectionId(this.inspection.id, this.inspection.title)
            .then(() => this.snackBar.open(this.translate.instant('inspectionUpsertDialog.brianSynchronizationActivationSuccess')));
            break;
          case 'upload':
            this.dataUpload();
            break;
        }
      });
  }

  /** Get a list of past reports belonging to the subject
   * @param filter
   */
  loadHistoryReports(filter: string): void {
    const page = { index: 1, size: 20 };
    const subjects = this.subjects4Inspections;
    const subjectModels = this.subjectModel4Inspections;

    let obReports: Observable<InspectionReportList>[];
    if (filter === 'subject') {
      obReports = subjects.map(subject => {
        const subFilter = new SubjectFilter(null, subject.id, null, null, null, null, null, null);
        return this.inspectionReportService.getInspectionReports(null, null, subFilter, null, null, page);
      });
    } else if (filter === 'subjectModel') {
      obReports = subjectModels.map(subjectModel => {
        const subFilter = new SubjectFilter(subjectModel.id, null, null, null, null, null, null, null);
        return this.inspectionReportService.getInspectionReports(null, null, subFilter, null, null, page);
      });
    } else if (filter === 'isOwn') {
      const obReport = this.inspectionReportService.getInspectionReports(null, null, null, true, null, page);
      obReports = [obReport];
    } else {
      const obReport = this.inspectionReportService.getInspectionReports(null, null, null, true, null, page);
      obReports = [obReport];
    }
    // Get past reports belonging to the subject
    // remove duplicates
    zip(...obReports)
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe(
        values => {
          let tempHistoryReports: InspectionReportListItem[] = [];
          values.forEach(reportList => {
            // Remove duplicates and merge report lists
            const tempReportList = reportList.items
              .filter(report => !tempHistoryReports.some(_ => _.reportId === report.reportId));

            tempHistoryReports = tempHistoryReports.concat(tempReportList);
          });

          // Sort by descending date and time of update
          tempHistoryReports = tempHistoryReports
            .sort((a, b) => new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime());
          this.historyReports = tempHistoryReports;
          this.isReportLoading = false;
        },
        error => {
          this.isReportLoading = false;
          this.snackBar.open(this.translate.instant('inspection.loadHistoryReportsError'));
          console.log(error);
        }
      );
  }

  /**
   * Get a list of past versions associated with a report
   */
  getReportVersions(): void {
    const reportVersionObs = this.generatedReports
      .map(report => this.inspectionReportService.getInspectionReportVersion(report.reportId));
    zip(...reportVersionObs)
      .pipe(take(1), takeUntil(this.onDestroy$))
      .subscribe(
        versionsList => this.genPastReportRevisions(versionsList),
        error => {
          this.snackBar.open(this.translate.instant('inspection.getReportVersionsError'));
          console.log(error);
          // this.isProcess = false;
        }
      );
  }

  genPastReportRevisions(versionsList: PastReportVersion[][]) {
    const tempGeneratedReportTree = [];

    // Create an array that concatenates the report and version list
    versionsList.forEach(versions => {
      const baseReportId = versions[0].reportId;
      const baseReport = this.generatedReports.find(report => report.reportId === baseReportId);

      let latestVersion = versions[0].version;
      if (versions.length > 1) {
        latestVersion = versions
          .map(_ => _.version)
          .reduce((a, b) => Math.max(a, b));
      }

      const revisions = versions.map(
        version => new GeneratedReport(version.reportId, version.version, version.title, version.status));
      revisions[0].status = baseReport.status;

      const node = new GeneratedReport();
      node.setData(baseReport, revisions, latestVersion);
      tempGeneratedReportTree.push(node);
    });

    this.generatedReportTree = tempGeneratedReportTree;
  }

  getSideContentHeight(): void {
    const sideContentElement = this.sideContents ? this.sideContents['_elementRef'] as ElementRef : null;
    this.sideContentHeight = sideContentElement ? sideContentElement.nativeElement.offsetHeight - 210 : 0;
  }

  /**
   * Display reuse confirmation dialog
   * @param  {string} reuseReportId
   * @returns void
   */
  openReuseConfirmDialog(reuseReportId: string): void {
    const dialogRef = this.dialog.open(ReuseConfirmDialogComponent, {
      width: '700px',
      height: '260px'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        this.reuseReport(reuseReportId);
      }
    });
  }

  /**
   * Reuse past reports
   * @param  {string} reuseReportId
   * @returns Promise
   */
  async reuseReport(reuseReportId: string): Promise<void> {
    this.oscSpinnerService.show();

    const reuseData = await this.inspectionReportService
      .getInspectionReport(reuseReportId)
      .toPromise()
      .then(value => value)
      .catch(error => {
        console.log(error);
        return null as InspectionReport;
      });

    if (!reuseData) {
      this.oscSpinnerService.hide();
      this.snackBar.open(this.translate.instant('inspection.reuseReportError'));
      return;
    }

    const reuseRequest = new InspectionReportReuse(
      this.report.reportId, reuseData.reportId, reuseData.version);

    this.inspectionReportService
      .putInspectionReportReuse(reuseRequest)
      .subscribe(
        res => this.checkReportStatus(res),
        (error: HttpErrorResponse) => {
          console.log(error);
          this.oscSpinnerService.hide();

          const message = error.status === 400
            ? this.translate.instant('inspection.reuseReport400Error')
            : this.translate.instant('inspection.reuseReportError');
          this.snackBar.open(message);
        },
        () => {
          this.snackBar.open(this.translate.instant('inspection.reuseReportSuccessfully'));
          this.oscSpinnerService.hide();
        }
      );
  }

  openSharedLockedConfirmDialog(isReport = false) {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: '550px',
      height: '250px',
      data: {
        firstMessage: this.translate.instant('commonConfirmDialog.sharedLockedMessage1'),
        secondMessage: isReport
          ? this.translate.instant('commonConfirmDialog.sharedLockedMessage3')
          : this.translate.instant('commonConfirmDialog.sharedLockedMessage2'),
        dialogType: 'information'
      }
    });
  }

  /**
   * @param expand
   * @param manual Operation by toggle button click
   */
  sideContentsToggle(expand?: boolean, manual: boolean = false) {
    if (manual) {
      this.manualToggled = true;
      this.sideNavOpened = expand;
      this.mainContentsStyle =
        this.sideNavOpened && !this.hasBackdrop ?
          this.mainContentsMarginLeftList["expand"]
          : this.mainContentsMarginLeftList["closed"];
    } else {
      this.manualToggled = this.sideNavOpened && this.autoExpand;
      this.sideNavOpened = this.autoExpand;
      this.mainContentsStyle =
        this.sideNavOpened ?
          this.mainContentsMarginLeftList["expand"]
          : this.mainContentsMarginLeftList["closed"];
    }
  }

  get hasBackdrop() {
    return window.innerWidth < this.screenMedium && this.sideNavOpened;
  }

  get autoExpand() {
    return window.innerWidth >= this.screenMedium;
  }

  createDraftPdfConfirmDialog(): MatDialogRef<CommonConfirmDialogComponent> {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: '500px',
      height: '230px',
      data: {
        firstMessage: this.translate.instant('inspectionReport.MsgRptInfoUpdated'),
        secondMessage: this.translate.instant('inspectionReport.MsgDraftRptUpdate'),
        dialogType: 'information'
      }
    });
  }

  createDraftPdfAndSHareConfirmDialog(): MatDialogRef<CommonConfirmDialogComponent> {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: '532px',
      height: '230px',
      data: {
        firstMessage: this.translate.instant('inspectionReport.MsgShareRptGenDraft1'),
        secondMessage: this.translate.instant('inspectionReport.MsgShareRptGenDraft2'),
        dialogType: 'information'
      }
    });
  }

  openShareDialog(): Observable<any> {
    this.inspDataManageService.shareReport = false;
    this.oscSpinnerService.show(true);
    const dialogRef = this.dialog.open(CommonShareDialogComponent, {
      width: '800px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { report: this.inspDataManageService.currentReport, isSummary: false }
    });
    return dialogRef.afterClosed();
  }
  /**
   * Create Draft PDF
   * @returns void
   */
  callCreatePDF(isPreview: boolean) {
    this.oscSpinnerService.show();

    let pdfOptions: PdfOptions;
    const configs = this.tenantConfigService.tenantConfig;

    const quality = isPreview
      ? this.report.draftQuality !== null ? this.report.draftQuality : configs.find(_ => _.key === TenantConfig.DefaultImageQualityOfDraftReports.KEY).value
      : this.report.finalQuality !== null ? this.report.finalQuality : configs.find(_ => _.key === TenantConfig.DefaultImageQualityOfFinalReports.KEY).value;

    this.measurementModeName.objectDistance = this.translate.instant('imageRemeasurementEdit.objectDistance');
    this.measurementModeName.distance = this.translate.instant('imageRemeasurementEdit.distance');
    this.measurementModeName.pointToLine = this.translate.instant('imageRemeasurementEdit.pointToLine');
    this.measurementModeName.depth = this.translate.instant('imageRemeasurementEdit.depth');
    this.measurementModeName.lines = this.translate.instant('imageRemeasurementEdit.lines');
    this.measurementModeName.area = this.translate.instant('imageRemeasurementEdit.area');

    this.reportTemplateService.getReportTemplate(this.report.templateId)
      .subscribe(
        reportTemplate => {
          this.setReport(this.report, false);
          pdfOptions = this.inspectionReportService.getPdfOptions(Number(quality), isPreview, reportTemplate.template.paperSize);
        },
        error => {
          console.log(error);
          pdfOptions = this.inspectionReportService.getPdfOptions(Number(quality), isPreview);
        },
        async () => {
          if (this.isOffline) {
            // Since detectChanges for change detection cannot be placed in Service,
            // PDF generation is separated from RP17 and processed by this component.
            const isSuccess = await this.createReportPdfInOffline(this.report);
            if (!isSuccess) {
              this.snackBar.open(this.translate.instant('inspectionReport.FailSave'));
              return;
            }
          }
          this.inspectionReportService
            .createReportPdfAsync(this.reportId, pdfOptions, this.measurementModeName, isPreview, null)
            .subscribe(
              report => {
                this.checkReportStatus(report);
                this.inspDataManageService.updateReportTabEmit(report);
                this.oscSpinnerService.hide();
                const title = report?.title || '';
                this.snackBar.open(this.translate.instant('inspectionReport.SuccessSave', { title: title }));
              },
              error => {
                console.log(error);
                this.oscSpinnerService.hide();
                this.snackBar.open(this.translate.instant('inspectionReport.FailSave'));
              }
            );
        }
      );
  }

  async createReportPdfInOffline(report: InspectionReport): Promise<boolean> {
    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    try {
      this.offlineReportPreviewService.isSaveHtml = true;
      this.offlineReportPreviewService.isPreview = false;
      this.changeDetectorRef.detectChanges();

      const option = { type: 'text/html' };
      const finalReportTitle = InspectionReport.getFinalReportTitle(report.title, report.version);
      const htmlFileName=finalReportTitle +'.html';

      const reportElem = document.getElementById('offline-draft-report');
      if (!report) {
        this.offlineReportPreviewService.isSaveHtml = false;
        this.changeDetectorRef.detectChanges();
        this.snackBar.open('load now');
        return false;
      }
      const snapshot = '<html><head><meta charset="utf-8">'
        + '<link rel="stylesheet" type="text/css" href="report.css">'
        + '</head><body style="-webkit-print-color-adjust: exact; background-color: none; margin: 0;">'
        + new XMLSerializer().serializeToString(reportElem)
        + '</body></html>';
      const reportBlob = new Blob([snapshot], option);
      const reportFile = new File([reportBlob], htmlFileName, option);

      const saveFolderPath = `inspections${pathSeparator}${report.inspectionIds[0]}${pathSeparator}reports${pathSeparator}${this.reportId}${pathSeparator}`;
      const savePath = saveFolderPath + htmlFileName;
      await this.offlineUtilsService.getAppDirectory()
        .toPromise()
        .then(dir => {
          const reportCssPath = `${dir}${pathSeparator}assets${pathSeparator}offline${pathSeparator}report.css`;
          return Promise.all([
            this.blobService.getBlobFromUrl(FILE_PREFIX + reportCssPath)
          ]);
        })
        .then(blobs => {
          const cssBlob = blobs[0];
          const cssFileName = 'report.css';
          const css = new File([cssBlob], cssFileName, { type: 'text/css' });
          return Promise.all([
            this.fileSystemAccessService.saveFile(css, saveFolderPath + cssFileName),
            this.fileSystemAccessService.saveFile(reportFile, saveFolderPath + htmlFileName)
          ]);
        });

      const pdfImageQuality = this.report.finalQuality as ReportImageQuality;

      await this.offlineUtilsService.printToPdf(savePath, { pdfImageQuality });

      this.offlineReportPreviewService.isSaveHtml = false;
      this.changeDetectorRef.detectChanges();

      return true;
    } catch (error) {
      console.error(error);
      this.offlineReportPreviewService.isSaveHtml = false;
      this.changeDetectorRef.detectChanges();

      return false;
    }
  }

  getPdfOptions(pdfFileName: string, paperSize: string) {
    const configs = this.tenantConfigService.tenantConfig;
    const draftQuality = configs.find(_ => _.key === TenantConfig.DefaultImageQualityOfFinalReports.KEY).value;
    const option = new WkHtmlToPdfOptions();

    option.title = pdfFileName;
    option.quality = PDF_COMPRESS_PARAMS_MEDIUM.QUALITY;
    option.imageQuality = PDF_COMPRESS_PARAMS_MEDIUM.QUALITY;
    if (draftQuality === 0) {
      option.dpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
      option.imageDpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
    } else if (draftQuality === 2) {
      option.dpi = PDF_COMPRESS_PARAMS_LOW.DPI;
      option.imageDpi = PDF_COMPRESS_PARAMS_LOW.DPI;
    } else {
      option.dpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
      option.imageDpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
    }

    switch (paperSize) {
      case 'A4':
        option.pageSize = PaperKind.A4;
        break;
      case 'B5':
        option.pageSize = PaperKind.B5;
        break;
      case 'B5JIS':
        option.pageSize = PaperKind.B5JIS;
        break;
      case 'Legal':
        option.pageSize = PaperKind.Legal;
        break;
      case 'Letter':
      default:
        option.pageSize = PaperKind.Letter;
    }

    return option;
  }

  /**
   * Set up reports. Check the status and perform processing.
   * @param  {InspectionReport} inspectionReport
   * @returns void
   */
  private checkReportStatus(inspectionReport: InspectionReport): void {
    clearTimeout(this.getReportTimer);

    switch (inspectionReport.status) {
      case INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING:
      case INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING:
        // If the report status is generating, re-fetch the report after a certain period of time
        this.getReportTimer = setTimeout(() => this.getInspectionReportPolling(inspectionReport.reportId), this.getReportTime);
        break;

      case INSPECTION_REPORT_STATUS_CODE.EDITING:
        if (this.isDraftProcessing) {
          // PDF generation completed
          if (this.inspDataManageService.shareReport)
            this.openShareDialog();
          const completedMessage = this.translate.instant('inspectionReport.CreateReportToastCompleted', { title: inspectionReport.title, version: inspectionReport.version });
          this.progressSnackBar.updateProgress(completedMessage, 100, true, inspectionReport.id);
          this.inspDataManageService.draftPdfIsAvailable = true;
        }
        break;

      case INSPECTION_REPORT_STATUS_CODE.COMPLETED:
        if (this.isFinalProcessing) {
          // PDF generation completed
          const completedMessage = this.translate.instant('inspectionReport.CreateReportToastCompleted', { title: inspectionReport.title, version: inspectionReport.version });
          this.progressSnackBar.updateProgress(completedMessage, 100, true, inspectionReport.id);
          this.getReportPdfUrl(inspectionReport);
          this.inspDataManageService.updateReportTabEmit(inspectionReport);
        }
        break;

      case INSPECTION_REPORT_STATUS_CODE.FAILED:
        if (this.isDraftProcessing || this.isFinalProcessing) {
          // Failed while generating PDF
          this.snackBar.open(this.translate.instant('inspectionReport.FailGenPDF'));
        }
        break;
    }

    this.report = inspectionReport;
    this.commonLayoutService.commonLayoutTitleWrapperEmit(this.inspection, this.report);
    this.inspDataManageService.currentReport = this.report;
  }

  public cancelGeneratePdf(isPreview: boolean) {
    this.inspectionReportService.getInspectionReport(this.reportId, this.report.version)
      .subscribe(
        report => {
          if (report.status === INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING || report.status === INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING) {
            const reportStatus = new ReportStatus();
            reportStatus.reportId = report.reportId;
            reportStatus.status = INSPECTION_REPORT_STATUS_CODE.EDITING;
            reportStatus.toPDFInstanceId = report.toPDFInstanceId;
            this.inspectionReportService.patchInspectionReportStatus(reportStatus, this.measurementModeName)
              .subscribe(
                res => this.report = res,
                error => {
                  this.snackBar.open(this.translate.instant('inspectionReport.ErrLoad'));
                  this.oscSpinnerService.hide();
                  console.log(error);
                },
                () => {
                  this.inspDataManageService.updateReportTabEmit(this.report);
                  const message = isPreview
                    ? this.translate.instant('inspectionReport.SuccessCancelGeneDraftPdf')
                    : this.translate.instant('inspectionReport.SuccessCancelGeneFinalPdf');
                  this.snackBar.open(message);
                }
              );
          }
        },
        error => {
          this.snackBar.open(this.translate.instant('inspectionReport.ErrLoad'));
          this.oscSpinnerService.hide();
          console.log(error);
        },
        () => {
          this.oscSpinnerService.hide();
        }
      );
  }

  getReportPdfUrl(inspectionReport: InspectionReport) {
    if (!inspectionReport?.reportId) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullRptID'));
    }

    const { reportId, version, title } = inspectionReport;

    this.pdfUrl = null;

    const fileName = this.inspectionReportService.isOnline ? InspectionReport.getTitleBasedPdfFileName(title) : undefined;

    this.inspectionReportService.getInspectionReportPdfUrl(reportId, version.toString(), false, fileName)
      .subscribe({
        next: (response) => {
          if (response?.url) {
            const pdfUrlWithConfig = this.networkStatusService.isBrianMode
              ? `${response?.url}#toolbar=0`
              : response?.url;
            this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrlWithConfig);
          }
          else {
            this.handleReportPdfUrlError(new Error('Invalid response when trying to fetch inspection report URL.'));
          }
          this.oscSpinnerService.hide();
        },
        error: (error: HttpErrorResponse) => {
          this.handleReportPdfUrlError(error);
        }
      });
  }

  private handleReportPdfUrlError(error : Error | HttpErrorResponse) {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      this.checkReportStatus(this.report);
    } else {
      console.log(error);
      this.snackBar.open(this.translate.instant('inspectionReport.ErrLoad'));
    }
    this.oscSpinnerService.hide();
  }

  getInspectionReportPolling(reportId: string, errorNum: number = 0) {
    let tmpReport: InspectionReport;
    this.getInspectionReport(reportId)
      .subscribe(
        report => {
          tmpReport = JSON.stringify(this.report) !== JSON.stringify(report)
            ? report
            : this.report;
        },
        error => {
          if (errorNum < 5) {
            setTimeout(() => {
              this.getInspectionReportPolling(reportId, errorNum++);
            }, this.getReportTime);
          } else {
            console.log(error);
            this.oscSpinnerService.hide();
          }
        },
        () => this.checkReportStatus(tmpReport)
      );
  }

  ngAfterViewInit(): void {
    // fix background issues
    document.getElementsByTagName('main')[0].classList.add('no-background');
  }

}
